import { toApiError } from "@services/api";
const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;

/** Where a calibration was recorded: this dashboard, or a run in the driver app. */
export type TCalibrationSource = "web" | "mobile";

/** The person who recorded a calibration (not necessarily the account owner). */
export type TCalibrationAuthor = {
  id: string;
  name: string | null;
  email: string;
};

export type TCalibration = {
  id: string;
  device: {
    id: string;
    name: string;
  };
  /**
   * The crop picked in the dialog (sent as `fieldId`). A driver-app
   * calibration has none — or a field the server detected from the track — so
   * the id may be null and the name empty (or the object missing).
   */
  culture?: {
    id: string | null;
    name: string | null;
  } | null;
  /** Raw stored id when the backend sends it; null for a driver-app run on no known field. */
  fieldId?: string | null;
  start: string;
  stop: string;
  /** 0 or null when no width was entered (driver-app runs): the machine's width timeline applies. */
  reaper_width: {
    value: number | null;
    unit: string;
  };
  weight: {
    value: number;
    unit: string;
  };
  /** Harvested area over the calibration window (speed-filtered); null until computed */
  area?: {
    value: number | null;
    unit: string;
  };
  /** Collected mass / harvested area, in kg/ha; null when either input is missing */
  estimated_yield?: {
    value: number | null;
    unit: string;
  };
  coefficient: number | null;
  coefficientUpdatedAt: string | null;
  /**
   * Workflow: pending | computing | completed | failed. A driver-app
   * calibration stays pending until the machine's telemetry for its window has
   * reached the server (spec §7), then computes like any other.
   */
  status?: string | null;
  error?: string | null;
  // Driver-app calibrations (spec §7). Optional so an older backend that omits
  // them still type-checks; a missing source means "web".
  source?: TCalibrationSource;
  /** Null for calibrations that predate "Recorded by" (or whose author was deleted). */
  recordedBy?: TCalibrationAuthor | null;
  /** The coefficient the phone computed itself — reference only, never used. */
  phoneCoefficient?: number | null;
};

export type TCreateCalibrationParams = {
  culture_id: string;
  start: string;
  stop: string;
  reaper_width: {
    value: number;
    unit: "m";
  };
  weight: {
    value: number;
    unit: "kg";
  };
};

export const getDeviceCalibrations = async (
  deviceId: string,
  perPage?: number
): Promise<Array<TCalibration>> => {
  const query = perPage != null ? `?perPage=${perPage}` : "";
  const url = `${BASE_URL}/api/v1/devices/${deviceId}/calibrations${query}`;
  const response = await fetch(url);
  if (!response.ok) throw await toApiError(response, "Failed to fetch calibrations");
  const body = await response.json();
  return body.calibrations ?? [];
};

export const createCalibration = async (
  deviceId: string,
  calibration: TCreateCalibrationParams
) => {
  const url = `${BASE_URL}/api/v1/devices/${deviceId}/calibrations`;
  // Capture the user's current timezone offset in seconds (e.g. UTC+3 = -10800 from getTimezoneOffset, we negate it)
  const deviceTimezoneSeconds = -new Date().getTimezoneOffset() * 60;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fieldId: calibration.culture_id,
      start: calibration.start,
      stop: calibration.stop,
      reaperWidthMeters: calibration.reaper_width.value,
      weightKilograms: calibration.weight.value,
      deviceTimezoneSeconds,
    }),
    mode: "cors",
  });
  if (!res.ok) {
    // Was err.message ?? err.toString(): the backend sends {error}, so the
    // user got "[object Object]".
    const err = await toApiError(res, "Failed to create calibration");
    console.error("Failed to create calibration", err);
    throw err;
  }
};

/**
 * An edit may leave the crop and the width out: the PATCH then omits them and
 * the stored values stay. A driver-app calibration has no crop and no width of
 * its own, and must not be forced to take the dialog's defaults.
 */
export type TEditCalibrationParams = Omit<
  TCreateCalibrationParams,
  "culture_id" | "reaper_width"
> & {
  culture_id?: string;
  reaper_width?: TCreateCalibrationParams["reaper_width"];
};

export const editCalibration = async (
  deviceId: string,
  calibrationId: string,
  calibration: TEditCalibrationParams
) => {
  const url = `${BASE_URL}/api/v1/devices/${deviceId}/calibrations/${calibrationId}`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    // Undefined keys drop out of the JSON, which the backend reads as "keep".
    body: JSON.stringify({
      fieldId: calibration.culture_id,
      start: calibration.start,
      stop: calibration.stop,
      reaperWidthMeters: calibration.reaper_width?.value,
      weightKilograms: calibration.weight.value,
    }),
    mode: "cors",
  });
  if (!res.ok) {
    // Was err.message ?? err.toString(): the backend sends {error}, so the
    // user got "[object Object]".
    const err = await toApiError(res, "Failed to edit calibration");
    console.error("Failed to edit calibration", err);
    throw err;
  }
};

export type TLatestCalibration = {
  coefficient: number;
  calibrationId: string;
} | null;

export const getLatestCalibration = async (
  deviceId: string
): Promise<TLatestCalibration> => {
  const url = `${BASE_URL}/api/v1/devices/${deviceId}/calibrations/latest`;
  const response = await fetch(url);
  if (!response.ok) {
    throw await toApiError(response, "Failed to fetch latest calibration");
  }
  const body = await response.json();
  return body.calibration;
};

export const getCalibrationStatus = async (
  deviceId: string,
  calibrationId: string
): Promise<{ id: string; status: string; coefficient: number | null; error: string | null }> => {
  const url = `${BASE_URL}/api/v1/devices/${deviceId}/calibrations/${calibrationId}/status`;
  const response = await fetch(url);
  if (!response.ok) {
    throw await toApiError(response, "Failed to fetch calibration status");
  }
  return response.json();
};

export const recomputeCalibration = async (
  deviceId: string,
  calibrationId: string
): Promise<void> => {
  const url = `${BASE_URL}/api/v1/devices/${deviceId}/calibrations/${calibrationId}/compute`;
  const res = await fetch(url, { method: "POST" });
  if (!res.ok) {
    throw await toApiError(res, "Failed to recompute calibration");
  }
};

export const deleteCalibration = async (
  deviceId: string,
  calibrationId: string
) => {
  const url = `${BASE_URL}/api/v1/devices/${deviceId}/calibrations/${calibrationId}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    mode: "cors",
  });
  if (!res.ok) {
    // Was err.message ?? err.toString(): the backend sends {error}, so the
    // user got "[object Object]".
    const err = await toApiError(res, "Failed to delete calibration");
    console.error("Failed to delete calibration", err);
    throw err;
  }
};
